import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Wordmark } from '../components/Logo';
import { errorCopy } from '../lib/errors';

export default function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [salonName, setSalonName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await signup({ email, password, name, salon_name: salonName });
    setSubmitting(false);
    if (res.ok) navigate('/app', { replace: true });
    else setError(errorCopy(res.error));
  }

  return (
    <main className="min-h-screen flex flex-col items-center px-6 py-10">
      <Link to="/" className="mb-12">
        <Wordmark className="text-ink" />
      </Link>

      <div className="w-full max-w-md">
        <h1 className="text-3xl font-semibold tracking-tighter mb-2">
          Armá tu Tijera en 1 minuto.
        </h1>
        <p className="text-ink/70 mb-8">
          Después tenés 4 minutos más para cargar tus servicios y barberos. 5 minutos en total. Te avisamos.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <Field label="Tu nombre" value={name} onChange={setName} placeholder="Juan Pérez" autoComplete="name" required />
          <Field label="Nombre del salón" value={salonName} onChange={setSalonName} placeholder="Barbería Don Carlos" required />
          <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="vos@barberia.com" autoComplete="email" required />
          <Field label="Contraseña" type="password" value={password} onChange={setPassword} placeholder="Mínimo 8 caracteres" autoComplete="new-password" required minLength={8} />

          {error && (
            <div className="text-sm text-danger bg-danger/5 border border-danger/20 rounded-lg px-4 py-3">
              {error}
            </div>
          )}

          <button type="submit" disabled={submitting} className="btn-primary w-full disabled:opacity-50">
            {submitting ? 'Bancá un toque…' : 'Crear mi salón'}
          </button>
        </form>

        <p className="mt-6 text-sm text-ash text-center">
          ¿Ya tenés cuenta?{' '}
          <Link to="/login" className="text-ink underline underline-offset-4">Entrá acá</Link>
        </p>
      </div>
    </main>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
}

function Field({ label, value, onChange, type = 'text', placeholder, autoComplete, required, minLength }: FieldProps) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink/80 mb-1.5 block">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        className="w-full rounded-lg border border-ink/15 bg-white px-4 py-3 text-ink placeholder:text-ash focus:border-forest focus:ring-2 focus:ring-forest/20 focus:outline-none transition"
      />
    </label>
  );
}
